import { reactive, ref } from 'vue'
import { githubApi } from '@/api/github'
import { withAsyncState } from '@/lib/asyncState'
import type { RepoLesson } from '@/types/models'

// Singleton, following useRepoRules.ts's shape: reactive Record keyed by
// repository-settings id, so lessons survive navigating away from and back
// to a repo's settings page within a session.
const lessonsByRepoSettings = reactive<Record<string, RepoLesson[]>>({})
const loading = ref(false)
const error = ref<string | null>(null)

export function useRepoLessons() {
  async function fetchLessons(installationId: string, repositorySettingsId: string): Promise<void> {
    if (!repositorySettingsId) return
    await withAsyncState(
      error,
      'Failed to load learned patterns.',
      async () => {
        lessonsByRepoSettings[repositorySettingsId] = await githubApi.listRepoLessons(
          installationId,
          repositorySettingsId,
        )
      },
      loading,
    )
  }

  function getLessons(repositorySettingsId: string): RepoLesson[] {
    return lessonsByRepoSettings[repositorySettingsId] ?? []
  }

  // Called on logout so the next session doesn't briefly show the previous
  // user's lessons before its own fetch resolves.
  function reset(): void {
    for (const key of Object.keys(lessonsByRepoSettings)) delete lessonsByRepoSettings[key]
    loading.value = false
    error.value = null
  }

  return {
    loading,
    error,
    fetchLessons,
    getLessons,
    reset,
  }
}
